import dayjs from "dayjs";
import { MouseEvent, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import CommonSelect from "../../../core/common/commonSelect";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import { paymentType } from "../../../core/common/selectoption/selectoption";
import { deleteAdmission } from "../../../service/admission";
import { all_routes } from "../../router/all_routes";
import { AdmissionData } from "./admission-list";
import { campusBrandName, campusBranchLabel } from "../../../utils/campusLogos";
import {
  payAdmissionFee,
  payOtherFee,
  updateFeeInvoiceItem,
} from "../../../service/fee-invoices";
import { useSelector } from "react-redux";
import { selectUser } from "../../../core/data/redux/slice/user";
import { selectBranches, selectCurrentBranch } from "../../../core/data/redux/slice/branchesSlice";
import { useReactToPrint } from "react-to-print";

type Props = {
  selectedId?: string | null;
  setSelectedId?: (id: string | null) => void;
  fetchData?: () => void;
  admissionData?: AdmissionData | null;
  testGroupData?: {
    testDateTime?: string;
  } | null;
};

const AdmissionModals = ({
  selectedId,
  setSelectedId,
  fetchData,
  admissionData,
}: Props) => {
  const [loading, setLoading] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const componentRef = useRef<HTMLDivElement>(null);
  const printRef = useRef<HTMLDivElement>(null);
  const [editData, setEditData] = useState<AdmissionData | null>(null);
  // Baseline to diff against for the unsaved-changes (dirty) check — updated
  // whenever a fresh record loads and again right after a successful save,
  // since the parent doesn't refresh admissionData after fetchData() runs.
  const [savedInvoice, setSavedInvoice] = useState<{
    discount: number;
    paymentType: string;
    notes: string;
  } | null>(null);
  const user = useSelector(selectUser);
  // Prefer the admission's OWN campus over whatever's globally selected in
  // the header — those can differ.
  const branches = useSelector(selectBranches);
  const currentBranch = useSelector(selectCurrentBranch);
  const recordBranch = branches.find((b) => b._id === editData?.branch) || currentBranch;
  const hasInvoiceData = !!editData?.invoice;
  const canEditFees =
    !!user.permissions?.includes("fees.edit") || !user.permissions?.length;
  const isDirty =
    !!savedInvoice &&
    ((editData?.invoice?.discount?.manual ?? 0) !== savedInvoice.discount ||
      (editData?.invoice?.paymentType || "cash") !== savedInvoice.paymentType ||
      (editData?.invoice?.notes || "") !== savedInvoice.notes);
  const invoiceGroups = editData?.feeGroups?.[0];
  const invoiceTypes = editData?.feeTypes?.[0];
  const firstInvoiceItem = editData?.invoice?.invoiceItems?.[0];
  
  const handleDelete = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!selectedId || loading) return;

    setLoading(true);
    try {
      await deleteAdmission(selectedId);
      setSelectedId?.(null);
      const el = document.getElementById("modal_confirm");
      if (el) {
        el.setAttribute("data-bs-dismiss", "modal");
        el.click();
        el.removeAttribute("data-bs-dismiss");
      }
      toast.success("Deleted successfully");
      fetchData?.();
    } catch (error: any) {
      console.error("Delete error:", error);

      const msg =
        error.response?.data?.message ||
        error.message || 
        "Failed to delete admission form!";

      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handlePayFees = async (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    if (!editData?.invoice?._id) {
      toast.error("Fee invoice not found");
      return;
    }
    setLoading(true);

    try {
      if (editData?.enrollment) {
        await payOtherFee(editData.invoice._id);
      } else {
        await payAdmissionFee(editData.invoice._id);
      }

      toast.success("Fees paid successfully!");
      fetchData?.();

      const el = document.getElementById("add_fees_collect");
      if (el) {
        el.setAttribute("data-bs-dismiss", "modal");
        el.click();
        el.removeAttribute("data-bs-dismiss");
      }
    } catch (error: any) {
      console.error("Payment error:", error);
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "An error occurred while processing payment"
      );
    } finally {
      setLoading(false);
    }
  };

  const handlePrintClick = () => {
    setShowPrintModal(true);
  };

  const handlePrintModalClose = () => {
    setShowPrintModal(false);
  };

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Fee-Receipt-${editData?.temporaryGR || ''}`,
    onAfterPrint: () => {
      setShowPrintModal(false);
    },
  });

  const calculateFinalAmount = () => {
    if (!editData?.invoice) return 0;
    
    const amount = Number(firstInvoiceItem?.amount || 0);
    const discount = Number(editData?.invoice?.discount?.manual || 0) + Number(editData?.invoice?.discount?.profile || 0);
    
    return amount - discount;
  };

  // Get user name from Redux user object
  const getUserDisplayName = () => {
    if (!user) return "System";
    
    // TypeScript safe access
    const userObj = user as any;
    
    if (userObj.firstName && userObj.lastName) {
      return `${userObj.firstName} ${userObj.lastName}`;
    } else if (userObj.name) {
      return userObj.name;
    } else if (userObj.username) {
      return userObj.username;
    } else if (userObj.email) {
      return userObj.email.split('@')[0];
    }
    
    return "System";
  };

  const routes = all_routes;

  useEffect(() => {
    if (admissionData) {
      setEditData(admissionData);
      setSavedInvoice({
        discount: admissionData.invoice?.discount?.manual ?? 0,
        paymentType: admissionData.invoice?.paymentType || "cash",
        notes: admissionData.invoice?.notes || "",
      });
    }
  }, [admissionData]);

  const handleSave = async (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (!isDirty || loading) return;

    if (!editData?.invoice?._id) {
      toast.error("Fee invoice not found");
      return;
    }

    const invoiceItemId = editData?.invoice?.invoiceItems?.[0]?._id;

    if (!invoiceItemId) {
      toast.error("Invoice item not found");
      return;
    }

    const discount = editData.invoice.discount?.manual || 0;
    const paymentType = editData.invoice.paymentType || "cash";
    const notes = editData.invoice.notes || "";

    setLoading(true);
    try {
      await updateFeeInvoiceItem(editData.invoice._id, invoiceItemId, {
        discount,
        notes,
        paymentType,
      });
      toast.success("Fee invoice updated successfully!");
      // Parent doesn't refresh admissionData after fetchData(), so re-baseline
      // locally against what we just saved rather than waiting for new props.
      setSavedInvoice({ discount, paymentType, notes });
      fetchData?.();
    } catch (error: any) {
      console.error("Update error:", error);
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "An error occurred while updating fee invoice"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Fees Collection Modal */}
      <div className="modal fade" id="add_fees_collect">
        <div
          className="modal-dialog modal-dialog-centered modal-lg printable-content"
          ref={componentRef}
        >
          <div className="modal-content">
            <div className="modal-header">
              <div className="d-flex align-items-center">
                <h4 className="modal-title">Fee Voucher</h4>
                <span className="badge badge-sm bg-primary ms-2">
                  {editData?.temporaryGR}
                </span>
              </div>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <form>
              <div id="modal-datepicker" className="modal-body">
                {editData && (
                  <div className="bg-light-300 p-3 pb-0 rounded mb-4">
                    <div className="row align-items-center">
                      <div className="col-lg-3 col-md-6">
                        <div className="d-flex align-items-center mb-3">
                          <Link
                            to={routes.studentGrid}
                            className="avatar avatar-md me-2"
                          >
                            <ImageWithBasePath
                              src={"assets/img/students/student-01.jpg"}
                              fullSrc={editData.studentPicture}
                              alt="img"
                            />
                          </Link>
                          <Link
                            to={routes.studentGrid}
                            className="d-flex flex-column"
                          >
                            <span className="text-dark">
                              {editData.firstName} {editData.lastName}
                            </span>
                            {editData.appliedClass?.name},{" "}
                            {editData.section?.name}
                          </Link>
                        </div>
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <div className="mb-3">
                          <span className="fs-12 mb-1">Total Outstanding</span>
                          <p className="text-dark">
                            {hasInvoiceData
                              ? (
                                  Number(firstInvoiceItem?.amount || 0) -
                                  Number(
                                    editData?.invoice?.discount?.manual || 0
                                  )
                                ).toLocaleString()
                              : "0"}
                          </p>
                        </div>
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <div className="mb-3">
                          <span className="fs-12 mb-1">Due Date</span>
                          <p className="text-dark">
                            {editData.invoice?.dueDate
                              ? dayjs(editData.invoice.dueDate).format(
                                  "DD MMM YYYY"
                                )
                              : "N/A"}
                          </p>
                        </div>
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <div className="mb-3">
                          <span
                            className={`badge ${
                              editData.invoice?.status?.toLowerCase() === "paid"
                                ? "badge-soft-success"
                                : "badge-soft-danger"
                            }`}
                          >
                            <i className="ti ti-circle-filled me-2" />
                            {editData.invoice?.status || "No Invoice"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {hasInvoiceData ? (
                  <div className="row">
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label className="form-label">Fees Group</label>
                        <input
                          type="text"
                          className="form-control"
                          value={invoiceGroups?.name || ""}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label className="form-label">Fees Type</label>
                        <input
                          type="text"
                          className="form-control"
                          value={invoiceTypes?.name || ""}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label className="form-label">Amount</label>
                        <input
                          type="text"
                          className="form-control"
                          value={firstInvoiceItem?.amount || ""}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label className="form-label">Discount</label>
                        <input
                          type="text"
                          className="form-control"
                          name="discount"
                          value={editData?.invoice?.discount?.manual ?? 0}
                          onChange={(e) => {
                            const newDiscount = Number(e.target.value) || 0;
                            const maxAmount = firstInvoiceItem?.amount || 0;

                            if (newDiscount > maxAmount) {
                              return;
                            }

                            setEditData((prev) => {
                              if (!prev || !prev.invoice) return prev;
                              return {
                                ...prev,
                                invoice: {
                                  ...prev.invoice,
                                  discount: {
                                    ...prev.invoice.discount,
                                    manual: newDiscount,
                                  },
                                },
                              };
                            });
                          }}
                          readOnly={!canEditFees}
                        />
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label className="form-label">Total Amount</label>
                        <input
                          type="text"
                          className="form-control"
                          value={(
                            Number(firstInvoiceItem?.amount || 0) -
                            Number(editData?.invoice?.discount?.manual || 0)
                          ).toString()}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label className="form-label">Payment Type</label>
                        <CommonSelect
                          className="select"
                          options={paymentType}
                          value={paymentType.find(
                            (item) =>
                              item.value ===
                              (editData?.invoice?.paymentType || "cash")
                          )}
                          onChange={(selectedOption) =>
                            setEditData((prev) => {
                              if (!prev || !prev.invoice) return null;
                              return {
                                ...prev,
                                invoice: {
                                  ...prev.invoice,
                                  paymentType: selectedOption.value,
                                },
                              };
                            })
                          }
                          isDisabled={!canEditFees}
                        />
                      </div>
                    </div>
                    <div className="col-lg-12">
                      <div className="mb-0">
                        <label className="form-label">Notes</label>
                        <textarea
                          rows={4}
                          className="form-control"
                          placeholder="Add Notes"
                          name="notes"
                          value={editData?.invoice?.notes || ""}
                          onChange={(e) =>
                            setEditData((prev) => {
                              if (!prev || !prev.invoice) return null;
                              return {
                                ...prev,
                                invoice: {
                                  ...prev.invoice,
                                  notes: e.target.value,
                                },
                              };
                            })
                          }
                          readOnly={!canEditFees}
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p>No fee record found for this admission.</p>
                  </div>
                )}
              </div>
              <div className="modal-footer pt-3">
                <Link
                  to="#"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                  onClick={() => setEditData(admissionData || null)}
                >
                  Cancel
                </Link>
                {hasInvoiceData && (
                  <>
                    <Link
                      to="#"
                      className={`btn btn-primary me-2 ${
                        isDirty ? "not-allowed opacity-50 pe-none" : ""
                      }`}
                      onClick={isDirty ? (e) => e.preventDefault() : handlePrintClick}
                      title={isDirty ? "Save your changes before printing" : undefined}
                    >
                      Print
                    </Link>
                    {editData?.invoice?.status?.toLowerCase() !== "paid" && (
                      <Link
                        to="#"
                        className={`btn btn-primary me-2 ${
                          (user.permissions?.includes("fees.collect") ||
                            !user.permissions?.length) &&
                          !isDirty
                            ? ""
                            : "not-allowed opacity-50 pe-none"
                        }`}
                        onClick={
                          (user.permissions?.includes("fees.collect") ||
                            !user.permissions?.length) &&
                          !isDirty
                            ? handlePayFees
                            : (e) => e.preventDefault()
                        }
                        title={
                          isDirty ? "Save your changes before paying" : undefined
                        }
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-1" />
                            Processing...
                          </>
                        ) : (
                          "Pay Fees"
                        )}
                      </Link>
                    )}
                    {canEditFees && (
                      <Link
                        to="#"
                        className={`btn btn-primary ${
                          !isDirty || loading ? "not-allowed opacity-50 pe-none" : ""
                        }`}
                        id="fee_payment_confirm"
                        onClick={handleSave}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-1" />
                            Saving...
                          </>
                        ) : (
                          "Save"
                        )}
                      </Link>
                    )}
                  </>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>

      {showPrintModal && (
        <div className="modal fade show main" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Fee Invoice</h5>
                <button
                  type="button"
                  className="btn-close custom-btn-close"
                  onClick={handlePrintModalClose}
                  aria-label="Close"
                >
                  <i className="ti ti-x" />
                </button>
              </div>

              <div className="modal-body">
                <div ref={printRef}>
                  <div className="invoice-print printable-content printable-main">
                    {/* Header */}
                    <div className="row mb-3">
                      <div className="col-6">
                        <p className="date">
                          Date: {dayjs((editData?.invoice as any)?.createdAt || new Date()).format("DD-MM-YYYY")}
                        </p>
                      </div>
                      <div className="col-6 text-end">
                        <p className="date">Receipt No: {editData?.invoice?.invoiceNumber || editData?.temporaryGR}</p>
                      </div>
                    </div>

                    <div className="text-center mb-2">
                      {recordBranch?.blackLogoUrl ? (
                        <img
                          src={recordBranch.blackLogoUrl}
                          alt={campusBrandName(recordBranch)}
                          style={{ width: "70px", height: "70px", objectFit: "contain" }}
                          className="mb-1"
                        />
                      ) : null}
                      <h4 className="text-uppercase fw-bold mb-1 heading">
                        {campusBrandName(recordBranch).toUpperCase()}
                      </h4>
                      {campusBranchLabel(recordBranch) ? (
                        <p className="branch-paragraph">{campusBranchLabel(recordBranch).toUpperCase()}</p>
                      ) : null}
                    </div>

                    <div className="mb-3 text-center">
                      <p className="date">
                        {editData?.firstName?.toUpperCase()} {editData?.lastName?.toUpperCase()}
                        {editData?.temporaryGR
                          ? ` (${editData.temporaryGR})`
                          : ""}
                      </p>
                      <p className="class">
                        Class {editData?.appliedClass?.name} -{" "}
                        {editData?.section?.name}
                      </p>
                    </div>

                    {/* Table */}
                    <table className="table mb-2">
                      <thead className="table-main">
                        <tr>
                          <th className="table-item">Month</th>
                          <th className="table-item">Details</th>
                          <th className="table-item">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {editData?.invoice?.invoiceItems?.map((item: any, index: number) => {
                          const monthDisplay =
                            item.month && item.month !== "other"
                              ? dayjs(item.month).format("MMM YYYY")
                              : "-";

                          return (
                            <tr key={index}>
                              <td className="table-item">{monthDisplay}</td>
                              <td className="table-item">
                                {`${item.invoice?.feeGroup?.name || "-"} - Fee`}
                              </td>
                              <td className="table-item-right">{item.amount} /=</td>
                            </tr>
                          );
                        })}

                        {((editData?.invoice?.discount?.profile || 0) +
                          (editData?.invoice?.discount?.manual || 0) > 0) && (
                          <tr>
                            <td className="table-item"></td>
                            <td className="table-item">Discount:</td>
                            <td className="table-item-right">
                              {(editData?.invoice?.discount?.profile || 0) +
                                (editData?.invoice?.discount?.manual || 0)}{" "}
                              /=
                            </td>
                          </tr>
                        )}

                        {/* {(editData?.invoice?.fine || 0) > 0 && (
                          <tr>
                            <td className="table-item"></td>
                            <td className="table-item">Fine:</td>
                            <td className="table-item-right">{editData?.invoice?.fine} /=</td>
                          </tr>
                        )} */}

                        <tr>
                          <td colSpan={2} className="total-left">
                            Total:
                          </td>
                          <td className="total-right">
                            {calculateFinalAmount()} /=
                          </td>
                        </tr>
                      </tbody>
                    </table>

                    {/* Note */}
                    <div className="mt-3">
                      <p className="note">
                        <strong>Note:</strong> Fee once deposited is not refundable
                        or adjustable.
                      </p>
                    </div>

                    {/* Generated By - Fixed for your user object */}
                    <div className="mt-2 text-center">
                      <p className="note">
                        <strong>
                          Generated By: {getUserDisplayName()}
                        </strong>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light me-2"
                  onClick={handlePrintModalClose}
                >
                  Cancel
                </button>
                <button type="button" className="btn btn-primary" onClick={handlePrint}>
                  <i className="ti ti-printer me-2"></i>
                  Print
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      <div className="modal fade" id="delete-modal">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body text-center">
              <span className="delete-icon">
                <i className="ti ti-trash-x" />
              </span>
              <h4>Confirm Deletion</h4>
              <p>
                You want to delete all the marked items, this cant be undone
                once you delete.
              </p>
              <div className="d-flex justify-content-center">
                <div className="btn btn-light me-3" data-bs-dismiss="modal">
                  Cancel
                </div>
                <button
                  type="button"
                  id="modal_confirm"
                  className="btn btn-danger"
                  onClick={handleDelete}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" />
                      Deleting...
                    </>
                  ) : (
                    "Yes, Delete"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdmissionModals;